import { PickingInfo } from "@deck.gl/core";
import { ObbAnnotation } from "../../api/Application";
import { CommandCardSlot } from "../../CommandCard";
import { VehicleColor } from "./VehicleColor";
import { VehicleType } from "./VehicleType";
import type { Feature, Geometry } from "geojson";
import { GeoJsonLayer } from "deck.gl";
import { VehicleExtra } from "./VehicleExtrasMode";
import { Pixel } from "../../common/Geography";

export enum Mode {
    Inactive,
    Create,
    Edit,
    VehicleType,
    VehicleColor,
    VehicleParts,
    VehicleExtras,
    Parking
}

export interface ModeInactive {
    mode: Mode.Inactive;
}

export interface ModeEdit {
    mode:       Mode.Edit;
    annotation: ObbAnnotation | undefined;
}

export interface ModeCreate {
    mode:         Mode.Create;
    classLabel:   number;
    initialWidth: number;
    rearCenter?:  [number, number]; // [lng, lat]
    frontCenter?: [number, number]; // [lng, lat]
}

export interface ModeVehicleType {
    mode:    Mode.VehicleType;
    type:    VehicleType;
    setTo?:  VehicleType;
    unsure?: ObbAnnotation; //when unsure we enter submode
}

export interface ModeVehicleColor {
    mode:  Mode.VehicleColor;
    color: VehicleColor; 
}

export interface ModeVehicleParts {
    mode:        Mode.VehicleParts;
    annotation:  ObbAnnotation | undefined;
    points:      number[]; 
    review:      boolean;
}

export enum ParkingNextClick {    
    FirstRoot,
    FirstStem,
    SecondRoot,
    LastRoot    
}

export interface ModeParking {
    mode: Mode.Parking;
    line:   Pixel[] | undefined;
    extent: Pixel   | undefined;
}

export interface ModeVehicleExtras {
    mode:  Mode.VehicleExtras;
    extra: VehicleExtra;
}

export type DrawingState = ModeInactive
                         | ModeCreate
                         | ModeEdit
                         | ModeVehicleType
                         | ModeVehicleColor
                         | ModeVehicleParts
                         | ModeVehicleExtras
                         | ModeParking;

/**
 * Context passed to each mode so it can read page state and call setters.
 */
export interface DrawingModeContext {
    imageWidth:      number;
    imageHeight:     number;
    selObj:          ObbAnnotation | undefined;
    hovObj:          ObbAnnotation | undefined;
    filterByMode:    boolean;
    render:          boolean;

    setDrawMode:     ( state: DrawingState ) => void;
    setSelObj:       ( ann: ObbAnnotation | undefined ) => void;
    setNewPolygon:   ( poly: [number, number][] ) => void;
    setFilterByMode: ( v: boolean ) => void;
    setRender:       ( v: boolean ) => void;
    setShowStats:    ( v: boolean ) => void;
    
    handleAdd:       ( ann: ObbAnnotation ) => Promise<void>;
    handleEdit:      ( ann: ObbAnnotation ) => Promise<void>;
    handleDelete:    ( ann: ObbAnnotation ) => Promise<void>;
}

/** Function table shared by all modes. */
export interface DrawingStateFns {
    getCommandSlots( mode: DrawingState ): ( CommandCardSlot | null )[];
    onKeyDown          ( drawMode: DrawingState, key:  string, shift: boolean, ctx: DrawingModeContext ): boolean;
    onDeckClick        ( drawMode: DrawingState, info: PickingInfo<Feature<Geometry, ObbAnnotation>>, ctx: DrawingModeContext ): void;
    onDeckHover        ( drawMode: DrawingState, info: PickingInfo<Feature<Geometry, ObbAnnotation>>, ctx: DrawingModeContext ): void;
    onAnnotationClick  ( drawMode: DrawingState, ann:  ObbAnnotation, ctx: DrawingModeContext ): void;
    filter             ( drawMode: DrawingState, ann:  ObbAnnotation ): boolean;
    
    //rendering
    getCursor(          drawMode: DrawingState ): string;
    getLineColor(       drawMode: DrawingState,       ann: ObbAnnotation, ctx: DrawingModeContext ): [number, number, number, number];
    getAnnotationLabel( drawMode: DrawingState, ann: ObbAnnotation ): string;
    getLayer?         ( drawMode: DrawingState, allAnnotations: ObbAnnotation[], ctx: DrawingModeContext ): GeoJsonLayer | null;
}